import React, { useRef, useState } from 'react';
import { Camera, X } from 'lucide-react';

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #e6f4eb 0%, #ffffff 100%)',
    padding: '20px',
  },
  videoContainer: {
    width: '100%',
    maxWidth: '500px',
    margin: '0 auto',
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
  },
  video: {
    width: '100%',
    height: 'auto',
    display: 'block',
  },
  canvas: {
    display: 'none',
  },
  captureButton: {
    background: '#dc2626',
    color: 'white',
    padding: '1rem 2rem',
    borderRadius: '12px',
    border: 'none',
    fontSize: '1.1rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    boxShadow: '0 4px 10px rgba(220, 38, 38, 0.3)',
    transition: 'all 0.3s ease',
  },
  message: {
    textAlign: 'center',
    padding: '1rem',
    borderRadius: '8px',
    margin: '1rem auto',
    maxWidth: '500px',
  },
  successMessage: {
    background: '#dcfce7',
    color: '#166534',
  },
  errorMessage: {
    background: '#fee2e2',
    color: '#991b1b',
  },
  closeButton: {
    background: 'white',
    color: '#4a5568',
    padding: '0.75rem',
    borderRadius: '12px',
    border: 'none',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 8px rgba(0, 0, 0, 0.15)',
      background: '#f1f5f9',
    },
    '&:active': {
      transform: 'translateY(1px)',
    },
  },
};

function CameraPage({onClose}) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [message, setMessage] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  React.useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, []);

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  const startCamera = async () => {
    try {
      const constraints = {
        video: {
          facingMode: { exact: "environment" } // This forces the back camera
        }
      };
      
      // Fallback to any available camera if back camera is not available
      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints)
        .catch(async () => {
          console.log('Back camera not found, falling back to default camera');
          return await navigator.mediaDevices.getUserMedia({ 
            video: true 
          });
        });

      videoRef.current.srcObject = mediaStream;
      setStream(mediaStream);
    } catch (err) {
      setMessage({ text: 'Error accessing camera: ' + err.message, type: 'error' });
    }
  };

  const captureImage = async () => {
    setIsLoading(true);
    setMessage(null);
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    
    const imageData = canvas.toDataURL('image/jpeg', 0.8);

    try {
      const response = await fetch('YOUR_PYTHON_API_ENDPOINT', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ image: imageData }),
      });

      const data = await response.json();
      // setMessage({ text: data.message, type: 'success' });
      setMessage({ text: "No result Found", type: 'success' });
    } catch (err) {
      setMessage({ text: "No result Found", type: 'error' });
      // setMessage({ text: 'Error processing image: ' + err.message, type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div style={styles.container}>
      <div style={styles.videoContainer}>
        <video
          ref={videoRef}
          style={styles.video}
          autoPlay
          playsInline
        />
        <canvas
          ref={canvasRef}
          style={styles.canvas}
        />
      </div>

      {message && (
        <div style={{
          ...styles.message,
          ...(message.type === 'success' ? styles.successMessage : styles.errorMessage)
        }}>
          {message.text}
        </div>
      )}
      <div style={{display:"flex",gap:"10px",marginTop:"10px"}}>
        <button
          onClick={captureImage}
          disabled={isLoading}
          style={{
            ...styles.captureButton,
            opacity: isLoading ? 0.7 : 1,
            cursor: isLoading ? 'not-allowed' : 'pointer',
          }}
        >
          <Camera size={20} />
          {isLoading ? 'Processing...' : 'Capture'}
        </button>
        <button onClick={handleClose} style={styles.closeButton}>
          Close
        </button>
      </div>
    </div>
  );
}

export default CameraPage;